import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  TrendingUp,
  Casino,
  History,
  AccountBalanceWallet,
  Assessment,
  AdminPanelSettings,
  MonetizationOn,
  AccountTree,
  Percent,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [betAnchorEl, setBetAnchorEl] = React.useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const isBetMenuOpen = Boolean(betAnchorEl);

  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryKey: ['wallet', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const response = await axios.get(`/wallet/user/${user.id}`);
      return response.data;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBetMenu = (event: React.MouseEvent<HTMLElement>) => {
    setBetAnchorEl(event.currentTarget);
  };

  const handleBetMenuClose = () => {
    setBetAnchorEl(null);
  };

  const handleBetNavigate = (path: string) => {
    navigate(path);
    handleBetMenuClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: <Dashboard /> },
    { path: '/results', label: 'Results', icon: <TrendingUp /> },
    { path: '/history', label: 'History', icon: <History /> },
    { path: '/withdrawal', label: 'Withdrawal', icon: <AccountBalanceWallet /> },
    { path: '/downline-management', label: 'Downline Management', icon: <AccountTree /> },
    { path: '/hierarchical-commission', label: 'Hierarchical Commission', icon: <AccountTree /> },
    { path: '/sales-management', label: 'Sales Management', icon: <Percent /> },
  ];

  if (user?.role === 'admin') {
    navItems.push({
      path: '/backoffice',
      label: 'Back Office',
      icon: <AdminPanelSettings />
    });
  }

  // On desktop, show all items. On mobile, use drawer.


  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleDrawerItemClick = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const drawerContent = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">ABC</Typography>
        <IconButton onClick={toggleDrawer(false)}>
          <MenuIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {navItems.map((item, index) => (
          <React.Fragment key={item.path}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleDrawerItemClick(item.path)}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
            {index === 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={handleBetMenu}
                    selected={['/enhanced-bet', '/mobile-bet'].includes(location.pathname)}
                  >
                    <ListItemIcon>
                      <Casino />
                    </ListItemIcon>
                    <ListItemText primary="Enhance Bet" />
                  </ListItemButton>
                </ListItem>
                <Divider sx={{ my: 1 }} />
              </>
            )}
          </React.Fragment>
        ))}
        <Divider sx={{ my: 1 }} />
        <ListItem>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <AccountCircle />
            <Typography variant="body2">{user?.username}</Typography>
          </Box>
        </ListItem>
        <ListItem>
          <Chip
            label={`Credits: $${walletData?.balance?.toFixed(2) || '0.00'}`}
            color="secondary"
            variant="outlined"
            sx={{ width: '100%' }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            ABC
          </Typography>
        
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {navItems.map((item, index) => (
                <React.Fragment key={item.path}>
                  <Button
                    color="inherit"
                    startIcon={item.icon}
                    onClick={() => navigate(item.path)}
                    sx={{
                      backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.1)' : 'transparent'
                    }}
                  >
                    {item.label}
                  </Button>
                  {index === 0 && (
                    <Button
                      color="inherit"
                      startIcon={<Casino />}
                      onClick={handleBetMenu}
                      sx={{
                        backgroundColor: ['/enhanced-bet', '/mobile-bet'].includes(location.pathname) ? 'rgba(255,255,255,0.1)' : 'transparent'
                      }}
                    >
                      Enhance Bet
                    </Button>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}

          <Menu
            anchorEl={betAnchorEl}
            open={isBetMenuOpen}
            onClose={handleBetMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => handleBetNavigate('/enhanced-bet')}>
              Classic Bet
            </MenuItem>
            <MenuItem onClick={() => handleBetNavigate('/mobile-bet')}>
              Mobile Bet
            </MenuItem>
          </Menu>
          
          {!isMobile && (
            <Chip
              label={`Credits: $${walletData?.balance?.toFixed(2) || '0.00'}`}
              color="secondary"
              variant="outlined"
              size="small"
              sx={{ 
                color: 'white', 
                borderColor: 'white',
                fontSize: '0.875rem'
              }}
            />
          )}
          
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <AccountCircle />
          </IconButton>
          
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>
              <Typography>{user?.username}</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Navbar;